import apiClient, { ApiResponse } from "./client";
import { config } from "../config";
import { notify } from "../utils/toast";

export interface ScholarshipTitle {
    id: string;
    titleName: string;
    notes: string;
    createdAt: string;
    updatedAt: string;
}

export interface ScholarshipTitleFilters {
    search?: string;
    page?: number;
    limit?: number;
}

export interface ScholarshipTitleListResponse {
    data: ScholarshipTitle[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
}

export interface BulkImportResponse {
    created: ScholarshipTitle[];
    count: number;
    errors?: { row: number; message: string }[];
}

export const scholarshipTitleApi = {
    // Get all scholarship titles with optional filters
    getAll: async (filters?: ScholarshipTitleFilters): Promise<ApiResponse<ScholarshipTitleListResponse>> => {
        try {
            const response = await apiClient.get<ScholarshipTitleListResponse>(config.endpoints.scholarshipTitles, filters);
            return response;
        } catch (error) {
            if (error instanceof Error) {
                notify.error(error.message);
            }
            console.warn("API call failed, using mock data:", error);
            throw error;
        }
    },

    // Get scholarship title by ID
    getById: async (id: string): Promise<ApiResponse<ScholarshipTitle>> => {
        try {
            const response = await apiClient.get<ScholarshipTitle>(`${config.endpoints.scholarshipTitles}/${id}`);
            return response;
        } catch (error) {
            if (error instanceof Error) {
                notify.error(error.message);
            }
            console.error("Error fetching scholarship title:", error);
            throw error;
        }
    },

    // Create new scholarship title
    create: async (title: Omit<ScholarshipTitle, "id" | "createdAt" | "updatedAt">): Promise<ApiResponse<ScholarshipTitle>> => {
        try {
            const response = await apiClient.post<ScholarshipTitle>(config.endpoints.scholarshipTitles, title);
            return response;
        } catch (error) {
            if (error instanceof Error) {
                notify.error(error.message);
            }
            console.error("Error creating scholarship title:", error);
            throw error;
        }
    },

    // Update scholarship title
    update: async (id: string, title: Partial<ScholarshipTitle>): Promise<ApiResponse<ScholarshipTitle>> => {
        try {
            const response = await apiClient.put<ScholarshipTitle>(`${config.endpoints.scholarshipTitles}/${id}`, title);
            return response;
        } catch (error) {
            if (error instanceof Error) {
                notify.error(error.message);
            }
            console.error("Error updating scholarship title:", error);
            throw error;
        }
    },

    // Delete scholarship title
    delete: async (id: string): Promise<ApiResponse<void>> => {
        try {
            const response = await apiClient.delete<void>(`${config.endpoints.scholarshipTitles}/${id}`);
            return response;
        } catch (error) {
            if (error instanceof Error) {
                notify.error(error.message);
            }
            console.error("Error deleting scholarship title:", error);
            throw error;
        }
    },

    // Bulk import scholarship titles
    bulkImport: async (titles: Omit<ScholarshipTitle, "id" | "createdAt" | "updatedAt">[]): Promise<ApiResponse<BulkImportResponse>> => {
        try {
            const response = await apiClient.post<BulkImportResponse>(`${config.endpoints.scholarshipTitles}/bulk-import`, titles);
            return response;
        } catch (error) {
            if (error instanceof Error) {
                notify.error(error.message);
            }
            console.error("Error bulk importing scholarship titles:", error);
            throw error;
        }
    },

    // Export scholarship titles
    export: async (filters?: ScholarshipTitleFilters): Promise<ApiResponse<ScholarshipTitle[]>> => {
        try {
            const response = await apiClient.get<ScholarshipTitle[]>(`${config.endpoints.scholarshipTitles}/export`, filters);
            return response;
        } catch (error) {
            if (error instanceof Error) {
                notify.error(error.message);
            }
            console.error("Error exporting scholarship titles:", error);
            throw error;
        }
    },

    // Check if scholarship title exists
    checkExist: async ({ searchValue }: { searchValue: string }) => {
        const response = await apiClient.get<boolean>(`${config.endpoints.scholarshipTitles}/check-exist`, { search_value: searchValue });
        return response;
    },
};